import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchShopProductButtonBuy } from "../actions/shopActions";
import { addProductCart, updateProductCart } from "../actions/cartActions";
import { deleteItemCart } from "../actions/cartActions";
import { RootState } from "../reducers";
import { TrashFill, Cart4 } from "react-bootstrap-icons";
import VariantPicker from "./VariantPicker";
import { useNavigate } from "react-router-dom";
import useTenant from "../tenant/useTenant";
import { VariantsData, VariantGroup } from "../actions/types";

interface Props {
  productId: number;
  onResolvedProductChange?: (resolvedProductId: number | null) => void;
}

const ProductButtonsBuy = (props: Props) => {
  const { productId, onResolvedProductChange } = props;
  const [selected, setSelected] = useState<Record<string, number | null>>({});
  const [isLoadingAdd, setIsLoadingAdd] = useState(false);
  const tenant = useTenant();

  const variantsData = useSelector(
    (state: RootState) => state.shop.variants[productId] as VariantsData | undefined,
  );
  const itemsCart = useSelector((state: RootState) => state.cart.itemsCart);
  const alertMessage = useSelector(
    (state: RootState) => state.cart.alertMessage,
  );
  const [quantity, setQuantity] = useState(1);
  const initializedRef = useRef(false);
  const dispatch: any = useDispatch();
  const navigate: any = useNavigate();

  const groups = variantsData?.groups ?? [];

  // Derived: resolve product from current selections (ignoring unselected groups)
  const resolvedProductId = useMemo<number | null>(() => {
    if (!variantsData || Object.keys(selected).length === 0) return null;
    const matches = Object.entries(variantsData.combinations).filter(([, combo]) =>
      Object.entries(selected).every(([gn, gt]) => {
        if (gt === null) return true;
        return combo[gn] === gt;
      }),
    );
    return matches.length === 1 ? Number(matches[0][0]) : null;
  }, [selected, variantsData]);

  // Product used for display: resolved variant or initial
  const displayProduct = useSelector(
    (state: RootState) => state.shop.product[resolvedProductId ?? productId],
  );
  const displayPathImages = useSelector(
    (state: RootState) => state.shop.pathImages[resolvedProductId ?? productId],
  );

  // Fetch product data (for variants, stock, price, etc.)
  useEffect(() => {
    const targetId = resolvedProductId || productId;
    if (targetId) {
      dispatch(fetchShopProductButtonBuy(targetId));
    }
    setIsLoadingAdd(false);
  }, [resolvedProductId, productId, itemsCart, alertMessage]);

  // Notify parent of the currently resolved variant product so it can
  // display the price/description/data of that exact product.
  useEffect(() => {
    onResolvedProductChange?.(resolvedProductId);
  }, [resolvedProductId, onResolvedProductChange]);

  useEffect(() => {
    displayProduct?.cantCart && setQuantity(displayProduct.cantCart);
    setIsLoadingAdd(false);
  }, [displayProduct]);

  const isOptionCompatible = useCallback(
    (groupName: string, typeId: number, currentSelected: Record<string, number | null>): boolean => {
      if (!variantsData) return false;
      const group = variantsData.groups.find(g => g.name === groupName);
      const option = group?.options.find(o => o.type_id === typeId);
      if (!option) return false;
      return option.product_ids.some(pid => {
        const combo = variantsData.combinations[String(pid)];
        if (!combo) return false;
        return Object.entries(currentSelected).every(([gName, gTypeId]) => {
          if (gName === groupName || gTypeId === null) return true;
          return combo[gName] === gTypeId;
        });
      });
    },
    [variantsData],
  );

  // Reset selection when the product changes (e.g. navigating to another product)
  const prevProductIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (prevProductIdRef.current !== productId) {
      prevProductIdRef.current = productId;
      initializedRef.current = false;
      setSelected({});
      setQuantity(1);
    }
  }, [productId]);

  // Initialize selected state when variants first load
  useEffect(() => {
    if (variantsData?.groups?.length && !initializedRef.current) {
      initializedRef.current = true;
      setSelected(Object.fromEntries(variantsData.groups.map(g => [g.name, null])));
    }
  }, [variantsData]);

  const handleSelect = (groupName: string, typeId: number) => {
    if (!variantsData) return;

    // Toggle: clicking already selected option deselects it
    if (selected[groupName] === typeId) {
      const newSelected = { ...selected, [groupName]: null };
      setSelected(newSelected);
      setQuantity(1);
      setIsLoadingAdd(false);
      return;
    }

    const newSelected = { ...selected, [groupName]: typeId };

    // Clear selections in other groups that are now invalid
    for (const gName of Object.keys(newSelected)) {
      if (gName === groupName || newSelected[gName] === null) continue;
      if (!isOptionCompatible(gName, newSelected[gName]!, newSelected)) {
        newSelected[gName] = null;
      }
    }

    setSelected(newSelected);
    setQuantity(1);
    setIsLoadingAdd(false);
  };

  // Build VariantGroup[] from VariantsData for VariantPicker
  const variantGroups: VariantGroup[] = useMemo(() => {
    return groups.map(group => {
      const options = group.options.map(opt => ({
        id: opt.type_id,
        label: opt.label,
        disabled: !isOptionCompatible(group.name, opt.type_id, selected),
      }));
      return { groupType: group.name, label: group.name, options };
    });
  }, [groups, selected, variantsData]);

  const onAddProductCart = (request: any) => {
    dispatch(addProductCart(request));
  };

  const onUpdateProductCart = (request: any) => {
    dispatch(updateProductCart(request));
  };

  const onDeleteItemCart = (id: string) => {
    setIsLoadingAdd(true);
    dispatch(deleteItemCart(id));
  };

  const handleSetQuantity = (element: any) => {
    setIsLoadingAdd(false);
    setQuantity(+element.target.value);
  };

  const handleSubmit = async () => {
    const id = displayProduct?.id;
    const name = displayProduct?.name;
    const price = displayProduct?.price;
    const image = displayPathImages?.[0] || null;
    const cantCart = displayProduct?.cantCart;
    const rowId = displayProduct?.rowId;
    if (quantity !== cantCart) {
      setIsLoadingAdd(true);
      if (cantCart) {
        onUpdateProductCart({ rowId, name, price, image, quantity });
      } else {
        onAddProductCart({ id, name, price, image, quantity });
      }
    }
  };

  // Buy button enabled: no variant groups OR a product is uniquely resolved
  const isComplete = groups.length === 0 || resolvedProductId !== null;

  const Skeleton = ({ style }: { style?: React.CSSProperties }) => (
    <div
      className="bg-light rounded"
      style={{
        height: 32,
        width: 80,
        animation: "sp-pulse 1.2s ease-in-out infinite",
        background: "linear-gradient(90deg,#eee,#f5f5f5,#eee)",
        backgroundSize: "200% 100%",
        ...style,
      }}
    />
  );

  const isProductLoaded = !!displayProduct;

  return (
    <div>
      <style>{`
        @keyframes sp-pulse { 0%{background-position:0% 50%} 100%{background-position:-200% 50%} }
      `}</style>

      {variantsData && variantGroups.length > 0 ? (
        <>
          {variantGroups.map((group) => (
            <div key={group.groupType} className="mb-3">
              <div className="mb-1 fw-semibold small">{group.label}</div>
              <VariantPicker
                group={group}
                selectedId={selected[group.label] ?? null}
                onSelect={(id) => handleSelect(group.label, Number(id))}
                activeColor={tenant.primaryColor}
                activeTextColor={tenant.primaryTextColor}
              />
            </div>
          ))}
          {!isComplete && (
            <div className="small text-muted mb-2">
              Seleccioná todas las variantes para agregar al carrito.
            </div>
          )}
        </>
      ) : isProductLoaded ? null : (
        !variantsData && (
          <div className="mb-3">
            <div className="mb-1 fw-semibold small" style={{ height: 14, width: 40, background: "#eee", borderRadius: 4 }} />
            <div className="d-flex gap-2 flex-wrap">
              <Skeleton style={{ width: 48 }} />
              <Skeleton style={{ width: 48 }} />
              <Skeleton style={{ width: 48 }} />
            </div>
          </div>
        )
      )}

      {isProductLoaded ? (
        displayProduct.stock > 0 ? (
          <div className="row mb-2 mt-2 g-1">
            <div className="col-4 text-end">
              <Form.Select
                name="quantity"
                id="quantity"
                value={quantity}
                onChange={handleSetQuantity}
                disabled={!isComplete}
                className="px-2"
              >
                {Array.from({ length: displayProduct.stock }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-8 d-flex justify-content-end align-items-start flex-wrap gap-1">
              {displayProduct.cantCart === 0 ? (
                <Button
                  variant="success"
                  onClick={handleSubmit}
                  disabled={isLoadingAdd || !isComplete}
                >
                  {isLoadingAdd ? "Agregando..." : "Agregar"}
                </Button>
              ) : (
                <>
                  <Button
                    variant="light"
                    onClick={handleSubmit}
                    disabled={isLoadingAdd || !isComplete}
                    className="border"
                  >
                    {isLoadingAdd ? "Modificando..." : "Modificar"}
                  </Button>
                  {itemsCart > 0 && (
                    <Button
                      onClick={() => navigate("/cart")}
                      variant="light"
                      className="border"
                    >
                      <Cart4 size={15} />
                      <span className="ms-1">{itemsCart}</span>
                    </Button>
                  )}
                  <Button
                    variant="danger"
                    onClick={() => onDeleteItemCart(displayProduct.rowId)}
                  >
                    <TrashFill size={15} />
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : (
          <Alert variant="info" className="py-2 px-3 small mt-2">
            Sin stock por el momento.
          </Alert>
        )
      ) : (
        <div className="row mb-2 mt-2 g-1">
          <div className="col-4 text-end">
            <Skeleton style={{ width: "100%" }} />
          </div>
          <div className="col-8 d-flex justify-content-end flex-wrap gap-1">
            <Skeleton style={{ width: 90 }} />
            <Skeleton style={{ width: 40 }} />
            <Skeleton style={{ width: 40 }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductButtonsBuy;
